/**
 * AsientosYGiros.jsx — el respaldo de los pasos 4 y 6 del día.
 *
 * OJO: DE LOS TRES BLOQUES, SÓLO `PedidosDelDia` SIGUE MONTADO (2026-08-25).
 *
 *   · `AsientosDelDia` — lo reemplazó «Respaldo en Odoo», que muestra lo mismo pero LE
 *     PREGUNTA A ODOO en vez de creerle a la marca guardada del ledger.
 *   · `GirosDelDia` — lo absorbió la «Cuadratura del día»: ahí está la compañía, el monto,
 *     la referencia del banco y quién lo registró, al lado de lo que se debía girar.
 *
 * Se dejan escritos y no borrados a propósito: la consolidación es de hoy y volver atrás
 * tiene que costar un import, no reescribirlos. Si en un mes siguen sin montarse, se
 * borran — un componente que nadie renderiza es de lo que después nadie sabe si está vivo.
 *
 * QUÉ ARREGLA. Los dos pasos decían un conteo y nada más: «9 asientos en Odoo», «girado a 2
 * compañías». Eso alcanza para saber si el paso está hecho, pero no para lo que se pregunta
 * después — qué asientos son, cuáles no llegaron a Odoo, y con qué referencia salió cada
 * transferencia. El detalle de asientos existía en la jornada vieja y se perdió al
 * reducirla; el de los giros nunca existió, aunque el dato está guardado desde la
 * migration 065.
 *
 * DOS BLOQUES Y NO UNO. Un asiento y un giro no son lo mismo: el asiento es el registro
 * contable del hecho, el giro es plata que salió del banco. Mezclarlos en una tabla
 * obligaría a una columna «tipo» para volver a separarlos al leer.
 *
 * NO CALCULA NADA. `contabilidad.por_tipo` y `cierre.giros` vienen resueltos de payment. La
 * única regla que vive aquí es de PRESENTACIÓN: qué tipos de asiento se pueden exportar a
 * Odoo y cuáles no, para no marcar como «pendiente» algo que nunca va a salir.
 *
 * @see payment/src/workers/dia.worker.ts — `contabilidad` y `cierre.giros`
 * @see payment/migrations/065_deposito_y_giro_por_dia.sql — dónde vive cada giro
 */
import React from 'react';

import { Cifra, Encabezado, Fila, Pie, Rotulo, Seccion } from './cierre/piezas';
import { Badge } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp, fmtFechaIso } from '../../../lib/yoolpay/formato';

const COLS_ASIENTOS = '1fr 90px 140px 1fr';
const COLS_GIROS = '1fr 130px 150px 110px 1fr';

/**
 * Los nombres de `finance.movements.tipo` en castellano.
 *
 * Un tipo que no esté acá se muestra tal cual y no se traduce a medias: `payout_bruto` es
 * feo pero es el nombre real de la fila, y adivinar una traducción haría que dos personas
 * llamen distinto a la misma cosa.
 */
const TIPO_LABEL = {
  payout_bruto: 'Pasivo con la compañía',
  fee_recaudacion: 'Fee de recaudación',
  fee_intermediacion: 'Fee de intermediación',
  psp_transitorio: 'Costo del PSP',
  iva_debito: 'IVA débito',
  retracto: 'Retracto',
};

/**
 * POR DÓNDE LLEGA CADA TIPO A ODOO. Los tres llegan; lo que cambia es el documento.
 *
 * Decía «no va a Odoo» y era falso: el fee se factura por PEDIDO DE VENTA y el costo del
 * PSP por PEDIDO DE COMPRA (migrations 059 y 063). Lo que no van es como ASIENTO, que es
 * otra cosa — y decir que no van manda a pensar que esa plata se quedó fuera de la
 * contabilidad.
 */
const VIA_ODOO = {
  payout_bruto: { texto: 'asiento', paso: 'paso 4' },
  fee_recaudacion: { texto: 'pedido de venta', paso: 'paso 5' },
  fee_intermediacion: { texto: 'pedido de venta', paso: 'paso 5' },
  psp_transitorio: { texto: 'pedido de compra', paso: 'paso 5' },
};

/** El estado de un tipo frente a Odoo, en una palabra y con su color. */
function estadoDeTipo(t) {
  if (t.con_error > 0) {
    return { texto: `${t.con_error} con error`, badge: 'vencido' };
  }
  // NO SINCRONIZABLE NO ES «NO VA A ODOO»: es que no va como ASIENTO. El fee se factura
  // por pedido de venta y el costo del PSP por pedido de compra, así que la vía se nombra
  // en vez de negar el destino.
  if (!t.sincronizable) {
    const via = VIA_ODOO[t.tipo];
    return {
      texto: via ? `vía ${via.texto}` : 'no va como asiento',
      badge: 'info',
      detalle: via ? `Se crea en el ${via.paso}` : null,
    };
  }
  if (t.pendientes > 0) {
    return { texto: `${t.pendientes} por contabilizar`, badge: 'pendiente' };
  }
  return { texto: 'en Odoo', badge: 'protegido' };
}

/**
 * Los asientos del día por tipo, con su estado frente a Odoo.
 *
 * Se muestran TODOS los tipos, incluidos los que no se exportan: el paso 4 cuenta 9 asientos
 * y aquí tienen que sumar 9, o la pantalla se estaría contradiciendo sola.
 */
export function AsientosDelDia({ contabilidad }) {
  const tipos = contabilidad?.por_tipo ?? [];

  return (
    <Seccion
      titulo="Asientos del día"
      bajada={
        'Lo que quedó escrito en el libro por los cobros de este día, por tipo. El pasivo con '
        + 'la compañía es el que sale a Odoo; los fees se facturan por pedido de venta.'
      }
      derecha={
        contabilidad ? (
          <span style={{ fontSize: 13, color: 'var(--yool-muted)' }}>
            {contabilidad.asientos} asiento{contabilidad.asientos === 1 ? '' : 's'}
            {contabilidad.pendientes > 0 ? ` · ${contabilidad.pendientes} por contabilizar` : ''}
          </span>
        ) : null
      }
    >
      {tipos.length === 0 ? (
        <p style={{ padding: 24, margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
          {/* Sin asientos NO es un error: un día cuyos cobros aún no pasaron por el outbox
              todavía no tiene libro. El paso 4 dice si eso es lo que corresponde. */}
          Ningún asiento escrito para los cobros de este día.
        </p>
      ) : (
        <>
          <Encabezado cols={COLS_ASIENTOS}>
            <Rotulo>Tipo</Rotulo>
            <Rotulo align="right">Asientos</Rotulo>
            <Rotulo align="right">Monto</Rotulo>
            <Rotulo>Estado en Odoo</Rotulo>
          </Encabezado>
          {tipos.map((t) => {
            const e = estadoDeTipo(t);
            return (
              <Fila key={t.tipo} cols={COLS_ASIENTOS}>
                <span style={{ fontSize: 13 }}>
                  {TIPO_LABEL[t.tipo] ?? t.tipo}
                  <span
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--yool-muted)',
                      fontFamily: 'ui-monospace, monospace',
                    }}
                  >
                    {t.tipo}
                  </span>
                </span>
                <Cifra>{t.asientos}</Cifra>
                {/* EL SIGNO SE RESPETA: los fees viven negativos en el libro porque se
                    descuentan del bruto. Mostrarlos en positivo aquí y en negativo en el
                    ledger sería la misma plata contada de dos formas. */}
                <Cifra color={t.monto < 0 ? CIFRA.ambar : CIFRA.tinta}>
                  {fmtClp(t.monto)}
                </Cifra>
                <span>
                  <Badge variant={e.badge} soft>{e.texto}</Badge>
                  {e.detalle ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: 'var(--yool-muted)',
                        marginTop: 3,
                      }}
                    >
                      {e.detalle}
                    </span>
                  ) : null}
                </span>
              </Fila>
            );
          })}
          <Pie
            totalLabel="Total escrito"
            total={fmtClp(contabilidad.monto)}
            totalColor={CIFRA.tinta}
          >
            <span style={{ fontSize: 12, color: 'var(--yool-muted)' }}>
              {contabilidad.exportados} exportado{contabilidad.exportados === 1 ? '' : 's'}
              {' · '}
              {contabilidad.pendientes} pendiente{contabilidad.pendientes === 1 ? '' : 's'}
              {contabilidad.con_error > 0 ? ` · ${contabilidad.con_error} con error` : ''}
              {contabilidad.no_aplica > 0 ? ` · ${contabilidad.no_aplica} no van a Odoo` : ''}
            </span>
          </Pie>
        </>
      )}
    </Seccion>
  );
}

const COLS_PEDIDOS = '1fr 150px 130px 1fr';

/** Qué significa el estado de Odoo, en castellano y con su color. */
function estadoPedido(p) {
  if (p.odoo_error) return { texto: 'falló', badge: 'vencido', nota: p.odoo_error };
  if (!p.odoo_name) return { texto: 'sin crear', badge: 'pendiente', nota: null };
  // BORRADOR NO ES CONFIRMADO, y es la distinción que cierra el día: el wizard de devengo
  // IGNORA los pedidos en borrador —no falla, no hace nada— así que el mes se daría por
  // cerrado con parte del resultado sin reconocer (trampa 5 de la skill de Odoo).
  if (p.odoo_estado === 'draft') {
    return {
      texto: 'en borrador',
      badge: 'pendiente',
      nota: 'Se confirma al día siguiente, cuando la jornada ya cerró y el monto no cambia.',
    };
  }
  if (p.devengado_at) return { texto: 'devengado', badge: 'protegido', nota: null };
  return { texto: 'confirmado', badge: 'protegido', nota: null };
}

/**
 * Los pedidos de venta y compra del día, con su estado en Odoo.
 *
 * QUÉ RESPONDE QUE EL PASO 5 NO. El paso dice «3 de 3 creados», que alcanza para saber que
 * la corrida pasó. No dice si están CONFIRMADOS, y ésa es la diferencia que cierra el día:
 * un pedido en borrador no se factura ni se devenga. Un mes con pedidos en borrador se
 * cierra igual y con el resultado incompleto, sin que nada lo diga.
 *
 * ES DONDE LOS FEES LLEGAN A ODOO. Los asientos de arriba muestran `fee_recaudacion` y
 * `fee_intermediacion` como «vía pedido de venta»; éstos son esos pedidos. El costo del PSP
 * llega por el de compra.
 */
export function PedidosDelDia({ pedidos }) {
  const filas = pedidos ?? [];
  const ventas = filas.filter((p) => p.tipo === 'venta');
  const compras = filas.filter((p) => p.tipo === 'compra');
  const enBorrador = filas.filter((p) => p.odoo_estado === 'draft').length;

  return (
    <Seccion
      titulo="Pedidos del día"
      bajada={
        'Los fees que Yool le factura a cada entidad (venta) y el costo que el PSP le factura '
        + 'a Yool (compra). Un pedido en borrador no se factura ni se devenga.'
      }
      derecha={
        filas.length > 0 ? (
          <span style={{ fontSize: 13, color: 'var(--yool-muted)' }}>
            {ventas.length} de venta · {compras.length} de compra
            {enBorrador > 0 ? ` · ${enBorrador} en borrador` : ''}
          </span>
        ) : null
      }
    >
      {filas.length === 0 ? (
        <p style={{ padding: 24, margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
          {/* Sin pedidos NO es un error: un día sin fees no deja nada que facturar. El paso 5
              dice si eso es lo que corresponde. */}
          Este día todavía no tiene pedidos creados.
        </p>
      ) : (
        <>
          <Encabezado cols={COLS_PEDIDOS}>
            <Rotulo>Pedido</Rotulo>
            <Rotulo align="right">Neto</Rotulo>
            <Rotulo>En Odoo</Rotulo>
            <Rotulo>Estado</Rotulo>
          </Encabezado>
          {filas.map((p, i) => {
            const e = estadoPedido(p);
            return (
              <Fila key={`${p.tipo}-${p.para}-${i}`} cols={COLS_PEDIDOS}>
                <span style={{ fontSize: 13 }}>
                  {p.tipo === 'venta' ? 'Venta' : 'Compra'}
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--yool-muted)' }}>
                    {p.tipo === 'venta'
                      ? `fee de ${p.para === 'digital' ? 'recaudación' : 'intermediación'}`
                      : `costo del PSP · ${p.para}`}
                  </span>
                </span>
                <Cifra>{p.monto_neto_clp == null ? '—' : fmtClp(p.monto_neto_clp)}</Cifra>
                <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                  {p.odoo_name ?? '—'}
                </span>
                <span>
                  <Badge variant={e.badge} soft>{e.texto}</Badge>
                  {e.nota ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: p.odoo_error ? '#B42318' : 'var(--yool-muted)',
                        marginTop: 3,
                      }}
                    >
                      {e.nota}
                    </span>
                  ) : null}
                </span>
              </Fila>
            );
          })}
        </>
      )}
    </Seccion>
  );
}

/**
 * Los giros que ya salieron ese día, con su respaldo.
 *
 * ES LA RESPUESTA A LA LLAMADA DE LA ASEGURADORA: «¿cuánto me giraron, cuándo y con qué
 * referencia?». El dato se guarda desde la migration 065 —`referencia` es obligatoria justo
 * para poder cruzarlo contra la cartola— y hasta ahora no se mostraba en ninguna parte.
 */
export function GirosDelDia({ giros }) {
  const filas = giros ?? [];
  const total = filas.reduce((s, g) => s + Number(g.monto_clp ?? 0), 0);

  return (
    <Seccion
      titulo="Giros del día"
      bajada={
        'Las transferencias que salieron a las compañías por los cobros de este día. Una por '
        + 'compañía: entra un depósito del PSP y salen N.'
      }
      derecha={
        filas.length > 0 ? (
          <span style={{ fontSize: 13, color: 'var(--yool-muted)' }}>
            {filas.length} transferencia{filas.length === 1 ? '' : 's'}
          </span>
        ) : null
      }
    >
      {filas.length === 0 ? (
        <p style={{ padding: 24, margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
          {/* Vacío NO significa que no correspondiera girar: significa que todavía no salió
              ninguna. Cuál de los dos casos es lo dice el paso 6, que sabe a quién le falta. */}
          Todavía no se registró ningún giro de este día.
        </p>
      ) : (
        <>
          <Encabezado cols={COLS_GIROS}>
            <Rotulo>Compañía</Rotulo>
            <Rotulo align="right">Monto girado</Rotulo>
            <Rotulo>Referencia</Rotulo>
            <Rotulo>Transferido</Rotulo>
            <Rotulo>Registrado por</Rotulo>
          </Encabezado>
          {filas.map((g) => (
            <Fila key={`${g.compania_id}-${g.referencia}`} cols={COLS_GIROS}>
              <span style={{ fontSize: 13 }}>
                {g.compania_nombre ?? (
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                    {g.compania_id}
                  </span>
                )}
                {/* POR DÓNDE SALIÓ cambia dónde reclamar si la compañía dice que no le
                    llegó: al PSP o al banco. */}
                <span style={{ display: 'block', fontSize: 11, color: 'var(--yool-muted)' }}>
                  vía {g.via === 'psp' ? 'el PSP' : 'transferencia manual'}
                </span>
              </span>
              <Cifra color={CIFRA.verde}>{fmtClp(g.monto_clp)}</Cifra>
              {/* La referencia en monoespaciada: es un número que se compara carácter por
                  carácter contra la cartola. */}
              <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                {g.referencia}
              </span>
              <span style={{ fontSize: 12 }}>{fmtFechaIso(g.fecha_transferencia)}</span>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--yool-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={g.registrado_por}
              >
                {g.registrado_por}
                {/* SI EL ASIENTO NO ESTÁ, SE DICE. La plata salió del banco, pero el pasivo
                    con la compañía sigue abierto en la contabilidad — que es exactamente lo
                    que se veía como «girado» mientras en Odoo no había nada. */}
                {g.odoo_move_id == null ? (
                  <span
                    style={{ display: 'block', color: '#B42318', fontWeight: 600 }}
                    title={g.odoo_error ?? 'El asiento no se posteó'}
                  >
                    sin asiento contable
                  </span>
                ) : null}
              </span>
            </Fila>
          ))}
          <Pie totalLabel="Total girado" total={fmtClp(total)} totalColor={CIFRA.verde} />
        </>
      )}
    </Seccion>
  );
}
